import { useCallback, useState } from "react";
import { styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import TooltipHelper from "components/TooltipHelper";
import type { UpsellFeature } from "types/upsell";
import useUpsellRequestAccess from "../../hooks/useUpsellRequestAccess";
import useUpsellAccessRequested from "../../hooks/useUpsellAccessRequested";
import UpsellRequestSuccessModal from "../UpsellRequestSuccessModal";

/**
 * `dark` — used on the mint top banner. `mint` — used on the black CTA panel.
 */
type UpsellRequestAccessButtonTone = "dark" | "mint";

/** Decorative, and kept out of the translations so it can't be mangled by translators. */
const BOLT_ICON = (
  <span role="img" aria-hidden="true">
    ⚡
  </span>
);

/**
 * The `primary` variant is black-on-white in light mode, which disappears into
 * the black CTA panel. The selector has to stay as specific as `Button`'s own
 * `&.MuiButton-primary` rule, otherwise those styles win.
 */
const MintButton = styled(Button)(({ theme }) => ({
  "&.MuiButton-primary": {
    color: theme.palette.common.black,
    backgroundColor: theme.palette.secondary1[60],

    "&:hover": {
      color: theme.palette.common.black,
      backgroundColor: theme.palette.secondary1[10],
    },
  },
}));

/**
 * A disabled button emits no pointer events, so the tooltip has to listen on a
 * wrapper. `inline-flex` keeps it from altering the surrounding layout.
 */
const TooltipTarget = styled("span")({
  display: "inline-flex",
});

interface UpsellRequestAccessButtonProps {
  feature: UpsellFeature;
  tone: UpsellRequestAccessButtonTone;
  /** Reported as the analytics `origin` so banner and CTA clicks can be told apart. */
  origin: string;
  size?: "small" | "medium";
}

function UpsellRequestAccessButton(props: UpsellRequestAccessButtonProps) {
  const { feature, tone, origin, size = "medium" } = props;

  const { t } = useTranslation();
  const { requestAccess, isRequesting } = useUpsellRequestAccess(feature, origin);
  const { hasRequested, markRequested } = useUpsellAccessRequested(feature);

  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const handleClick = useCallback(async () => {
    try {
      await requestAccess();
      markRequested();
      setIsSuccessOpen(true);
    } catch {
      /**
       * `useMutation` already surfaces the failure via its error state, and the
       * request stays retryable — so swallow it here rather than letting an
       * unhandled rejection escape the click handler. Deliberately does not mark
       * the feature as requested, so a failed attempt can be retried.
       */
    }
  }, [requestAccess, markRequested]);

  const handleSuccessClose = useCallback(() => setIsSuccessOpen(false), []);

  const ToneButton = tone === "mint" ? MintButton : Button;

  return (
    <>
      <TooltipHelper title={hasRequested ? t("upsell.access_already_requested_label") : null}>
        <TooltipTarget>
          <ToneButton
            variant="primary"
            size={size}
            onClick={handleClick}
            loading={isRequesting}
            disabled={hasRequested}
            startIcon={BOLT_ICON}
          >
            {t("upsell.request_access_label")}
          </ToneButton>
        </TooltipTarget>
      </TooltipHelper>
      <UpsellRequestSuccessModal open={isSuccessOpen} onClose={handleSuccessClose} />
    </>
  );
}

export default UpsellRequestAccessButton;
